/**
 * WiringReport Component
 *
 * Main wiring report component that orchestrates all sub-components.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Loader2, Cable } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { ReportContainer } from '../primitives/ReportContainer';
import { ReportHeader } from '../primitives/ReportHeader';
import { WiringSummaryCards } from './WiringSummaryCards';
import { CableRequirementsTable } from './CableRequirementsTable';
import { CablePathsTable } from './CablePathsTable';
import { JunctionBoxesTable } from './JunctionBoxesTable';
import { useWiringReportData } from '@/hooks/reports/useWiringReportData';
import {
  exportWiringReportToPDF,
  exportWiringReportToCSV,
} from '@/services/reports/reportExportService';
import type { ReportFilters, WiringReportSection } from '@/types/reports';

interface WiringReportProps {
  projectId: string;
  projectName: string;
  clientName?: string;
  initialFloorPlanId?: string | 'all';
}

const CSV_SECTIONS: { id: WiringReportSection; label: string }[] = [
  { id: 'cable-requirements', label: 'Cable Requirements' },
  { id: 'paths', label: 'Path Details' },
  { id: 'junctions', label: 'Junctions' },
];

export function WiringReport({
  projectId,
  projectName,
  clientName,
  initialFloorPlanId = 'all',
}: WiringReportProps) {
  const [filters, setFilters] = useState<ReportFilters>({
    floorPlanId: initialFloorPlanId,
    groupBy: 'floor-plan',
  });
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('requirements');

  useEffect(() => {
    setFilters(prev => ({ ...prev, floorPlanId: initialFloorPlanId }));
  }, [initialFloorPlanId]);

  const { data, isLoading, error, floorPlans } = useWiringReportData({
    projectId,
    filters,
  });

  const handleFloorPlanChange = useCallback((id: string | 'all') => {
    setFilters(prev => ({ ...prev, floorPlanId: id }));
  }, []);

  const handleExportPDF = useCallback(async () => {
    if (!data) return;

    setIsExporting(true);
    toast.info('Generating PDF...');

    try {
      await exportWiringReportToPDF(data, {
        projectName,
        clientName,
        floorPlanFilter:
          filters.floorPlanId === 'all'
            ? 'All Floor Plans'
            : (floorPlans.find(fp => fp.id === filters.floorPlanId)?.name ?? 'Unknown'),
      });
      toast.success('PDF exported successfully');
    } catch (err) {
      logger.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  }, [data, projectName, clientName, filters.floorPlanId, floorPlans]);

  const handleExportCSV = useCallback(
    async (section?: string) => {
      if (!data || !section) return;

      setIsExporting(true);

      try {
        exportWiringReportToCSV(data, section as WiringReportSection, projectName);
        toast.success('CSV exported successfully');
      } catch (err) {
        logger.error('CSV export error:', err);
        toast.error('Failed to export CSV');
      } finally {
        setIsExporting(false);
      }
    },
    [data, projectName],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading report data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="font-medium text-destructive">Failed to load report data</p>
        <p className="text-sm text-muted-foreground mt-1">
          Something went wrong while loading the wiring report. Try switching floor plans or
          reloading the page.
        </p>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <Cable className="h-12 w-12 mb-4" />
        <p className="font-medium">No wiring data available</p>
        <p className="text-sm mt-1">Add cable paths and junctions to your floor plans</p>
      </div>
    );
  }

  const selectedFloorPlanName =
    filters.floorPlanId === 'all'
      ? 'All Floor Plans'
      : (floorPlans.find(fp => fp.id === filters.floorPlanId)?.name ?? 'Unknown');

  return (
    <ReportContainer
      title="Wiring Report"
      projectName={projectName}
      clientName={clientName}
      floorPlanFilter={selectedFloorPlanName}
    >
      {/* Header with filters and actions */}
      <ReportHeader
        title="Wiring Report"
        subtitle="Cable requirements, paths, and junction details"
        floorPlans={floorPlans}
        selectedFloorPlanId={filters.floorPlanId}
        onFloorPlanChange={handleFloorPlanChange}
        onExportPDF={handleExportPDF}
        onExportCSV={handleExportCSV}
        csvSections={CSV_SECTIONS}
        isExporting={isExporting}
        className="mb-6"
      />

      {/* Summary Cards */}
      <div className="mb-6">
        <WiringSummaryCards summary={data.summary} />
      </div>

      {/* Tabbed Content */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 print:hidden">
              <TabsTrigger value="requirements">Cable Requirements</TabsTrigger>
              <TabsTrigger value="paths">Path Details</TabsTrigger>
              <TabsTrigger value="junctions">Junctions</TabsTrigger>
            </TabsList>

            {/* Screen view - show active tab only */}
            <div className="print:hidden">
              <TabsContent value="requirements" className="mt-0">
                <CableRequirementsTable
                  data={data.cableRequirements}
                  groupByFloorPlan={filters.floorPlanId === 'all'}
                />
              </TabsContent>

              <TabsContent value="paths" className="mt-0">
                <CablePathsTable
                  data={data.pathDetails}
                  groupByFloorPlan={filters.floorPlanId === 'all'}
                />
              </TabsContent>

              <TabsContent value="junctions" className="mt-0">
                <JunctionBoxesTable
                  data={data.junctions}
                  groupByFloorPlan={filters.floorPlanId === 'all'}
                />
              </TabsContent>
            </div>

            {/* Print view - show all sections */}
            <div className="hidden print:block space-y-8">
              <div className="report-tab-content">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b-2 border-primary">
                  Cable Requirements
                </h3>
                <CableRequirementsTable
                  data={data.cableRequirements}
                  groupByFloorPlan={filters.floorPlanId === 'all'}
                />
              </div>

              <div className="report-tab-content">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b-2 border-primary">
                  Path Details
                </h3>
                <CablePathsTable
                  data={data.pathDetails}
                  groupByFloorPlan={filters.floorPlanId === 'all'}
                />
              </div>

              <div className="report-tab-content">
                <h3 className="text-lg font-semibold mb-4 pb-2 border-b-2 border-primary">
                  Junction Boxes
                </h3>
                <JunctionBoxesTable
                  data={data.junctions}
                  groupByFloorPlan={filters.floorPlanId === 'all'}
                />
              </div>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </ReportContainer>
  );
}
